import { useTranslation } from "react-i18next";
import React from "react";
import { MenuItem } from "primereact/menuitem";
import { SidebarIcon } from "../SidebarIcon/SidebarIcon";
import myFolioIcon from "../assets/my-folio.svg";
import vettingIcon from "../assets/vetting.svg";
import myReportsIcon from "../assets/my-reports.svg";
import quotes from "../assets/quotes.svg";
import orders from "../assets/orders.svg";
import myVesselsIcon from "../assets/vessels.svg";
import gradeIcon from "../assets/scatter-icon.svg";
import subgradeIcon from "../assets/horizontal-bar-icon.svg";

export type NavSidebarItem = {
    text: string;
    logo?: string;
    image?: string;
    id: string;
    to: string;
    callback: (id: string) => void;
};
export type AppSidebarGroup = {
    id: string;
    groupName: string;
    items: Array<NavSidebarItem>;
};
export type AppSidebarProps = {
    activeItem: string;
    groups: Array<AppSidebarGroup>;
};

export const buildNavigationMenus = (
    additionalCapabilities: Record<string, boolean>,
    monitoring: {
        links: {
            myVesselsLink: string;
            defectsLink: string;
            gradesLink: string;
            subgradesLink: string;
            rectificationsLink: string;
        };
        flags: {
            showMonitoringDefects: boolean;
            showMonitoringGrades: boolean;
            showMonitoringMyVessels: boolean;
            showMonitoringSubGrades: boolean;
        };
        callbacks: {
            clickMonitoringDefects: (e: string) => void;
            clickMonitoringGrades: (e: string) => void;
            clickMonitoringMyVessels: (e: string) => void;
            clickMonitoringSubGrades: (e: string) => void;
            clickMonitoringRetifications: (e: string) => void;
        };
    },
    inspections: {
        links: {
            myReportsLink: string;
            inspectionQuotesLink: string;
            inspectionOrdersLink: string;
        };
        flags: {
            showInspectionsMyReports: boolean;
            showOrders: boolean;
            showInspectionsQuotes: boolean;
        };
        callbacks: {
            clickInspectionsMyReports: (e: string) => void;
            clickInspectionsQuotes: (e: string) => void;
            clickInspectionsOrders: (e: string) => void;
        };
    },
    screening: {
        links: {
            myPortfolioLink: string;
            screeningLink: string;
        };
        flags: {
            showScreeningMyPortfolio: boolean;
            showScreeningVetting: boolean;
        };
        callbacks: {
            clickScreeningMyPortfolio: (e: string) => void;
            clickScreeningVetting: (e: string) => void;
        };
    },
    activePath: string
): {
    appSidebarItems: Array<AppSidebarGroup>;
    idwalSidebarItems: {
        title: string;
        items: Array<MenuItem>;
    };
} => {
    const { t } = useTranslation("common");

    const isActive = (to: string) => to === activePath;

    const activeIconColor = "#008094";
    const nonActiveIconColor = "#777777";

    const appSidebarItems: Array<AppSidebarGroup> = [
        {
            groupName: t("appNavigation.screening"),
            id: "screening",
            items: [
                ...(screening.flags.showScreeningMyPortfolio
                    ? [
                          {
                              text: t("appNavigation.myFolio"),
                              image: myFolioIcon,
                              id: "my-folio",
                              callback: (to: string) =>
                                  screening.callbacks.clickScreeningMyPortfolio(
                                      to
                                  ),
                              to: screening.links.myPortfolioLink
                          }
                      ]
                    : []),
                ...(screening.flags.showScreeningVetting
                    ? [
                          {
                              text: t("appNavigation.vetting"),
                              image: vettingIcon,
                              id: "vetting",
                              callback: (to: string) =>
                                  screening.callbacks.clickScreeningMyPortfolio(
                                      to
                                  ),
                              to: screening.links.screeningLink
                          }
                      ]
                    : [])
            ]
        },
        {
            groupName: t("appNavigation.inspections"),
            id: "inspections",
            items: [
                ...(inspections.flags.showInspectionsMyReports
                    ? [
                          {
                              text: t("appNavigation.myReports"),
                              image: myReportsIcon,
                              id: "my-reports",
                              callback: (to: string) =>
                                  inspections.callbacks.clickInspectionsMyReports(
                                      to
                                  ),
                              to: inspections.links.myReportsLink
                          }
                      ]
                    : []),
                ...(inspections.flags.showOrders
                    ? [
                          {
                              text: t("appNavigation.orders"),
                              image: orders,
                              id: "orders",
                              callback: (to: string) =>
                                  inspections.callbacks.clickInspectionsOrders(
                                      to
                                  ),
                              to: inspections.links.inspectionOrdersLink
                          }
                      ]
                    : []),
                ...(inspections.flags.showInspectionsQuotes
                    ? [
                          {
                              text: t("appNavigation.quotes"),
                              image: quotes,
                              id: "quotes",
                              callback: (to: string) =>
                                  inspections.callbacks.clickInspectionsQuotes(
                                      to
                                  ),
                              to: inspections.links.inspectionQuotesLink
                          }
                      ]
                    : [])
            ]
        },
        {
            groupName: t("appNavigation.monitoring"),
            id: "monitoring",
            items: [
                ...(monitoring.flags.showMonitoringMyVessels &&
                additionalCapabilities["CAP_FLEETAPP_VIEW_FLEETS"] === true
                    ? [
                          {
                              text: t("appNavigation.myFleet"),
                              image: myVesselsIcon,
                              id: "my-vessels",
                              callback: (to: string) =>
                                  monitoring.callbacks.clickMonitoringMyVessels(
                                      to
                                  ),
                              to: monitoring.links.myVesselsLink
                          }
                      ]
                    : []),
                ...(monitoring.flags.showMonitoringGrades &&
                additionalCapabilities["CAP_FLEETAPP_VIEW_GRADES"] === true
                    ? [
                          {
                              text: t("appNavigation.grade"),
                              image: gradeIcon,
                              id: "grade",
                              callback: (to: string) =>
                                  monitoring.callbacks.clickMonitoringGrades(
                                      to
                                  ),
                              to: monitoring.links.gradesLink
                          }
                      ]
                    : []),
                ...(monitoring.flags.showMonitoringSubGrades &&
                additionalCapabilities["CAP_FLEETAPP_VIEW_SUBGRADES"] === true
                    ? [
                          {
                              text: t("appNavigation.subGrade"),
                              image: subgradeIcon,
                              id: "subGrade",
                              callback: (to: string) =>
                                  monitoring.callbacks.clickMonitoringSubGrades(
                                      to
                                  ),
                              to: monitoring.links.subgradesLink
                          }
                      ]
                    : []),
                ...(monitoring.flags.showMonitoringDefects &&
                additionalCapabilities["CAP_FLEETAPP_VIEW_DEFECTS"] === true
                    ? [
                          {
                              text: t("appNavigation.defects"),
                              logo: "pi pi-exclamation-triangle",
                              id: "defects",
                              callback: (to: string) =>
                                  monitoring.callbacks.clickMonitoringDefects(
                                      to
                                  ),
                              to: monitoring.links.defectsLink
                          }
                      ]
                    : []),
                ...(additionalCapabilities[
                    "CAP_FLEETAPP_VIEW_RECTIFICATIONS"
                ] === true
                    ? [
                          {
                              text: t("appNavigation.rectifications"),
                              logo: "pi pi-exclamation-triangle",
                              id: "rectifications",
                              callback: (to: string) =>
                                  monitoring.callbacks.clickMonitoringRetifications(
                                      to
                                  ),
                              to: monitoring.links.rectificationsLink
                          }
                      ]
                    : [])
            ]
        }
    ];

    const idwalSidebarIconStyle =
        "width: 22px; height: 22px; margin-right: 0.4rem; margin-left: -0.25rem";

    const idwalSidebarItems = {
        title: t("idwalSidebar.idPlatform"),
        items: [] as Array<MenuItem>
    };

    if (
        screening.flags.showScreeningMyPortfolio ||
        screening.flags.showScreeningVetting
    ) {
        idwalSidebarItems.items?.push({
            id: "1",
            label: t("idwalSidebar.screen"),
            items: [
                ...(screening.flags.showScreeningMyPortfolio
                    ? [
                          {
                              id: "1-1",
                              label: t("appNavigation.myFolio"),
                              className: isActive(
                                  screening.links.myPortfolioLink
                              )
                                  ? "active"
                                  : "",
                              icon: (
                                  <SidebarIcon
                                      src={myFolioIcon}
                                      fill={
                                          isActive(
                                              screening.links.myPortfolioLink
                                          )
                                              ? activeIconColor
                                              : nonActiveIconColor
                                      }
                                      style={idwalSidebarIconStyle}
                                  />
                              ),
                              command: () =>
                                  screening.callbacks.clickScreeningMyPortfolio(
                                      screening.links.myPortfolioLink
                                  )
                          }
                      ]
                    : []),
                ...(screening.flags.showScreeningVetting
                    ? [
                          {
                              id: "1-2",
                              label: t("appNavigation.vetting"),
                              className: isActive(screening.links.screeningLink)
                                  ? "active"
                                  : "",
                              icon: (
                                  <SidebarIcon
                                      src={vettingIcon}
                                      fill={
                                          isActive(
                                              screening.links.screeningLink
                                          )
                                              ? activeIconColor
                                              : nonActiveIconColor
                                      }
                                      style={idwalSidebarIconStyle}
                                  />
                              ),
                              command: () =>
                                  screening.callbacks.clickScreeningVetting(
                                      screening.links.screeningLink
                                  )
                          }
                      ]
                    : [])
            ]
        });
    }

    if (
        inspections.flags.showInspectionsMyReports ||
        inspections.flags.showInspectionsQuotes ||
        inspections.flags.showOrders
    ) {
        idwalSidebarItems.items?.push({
            id: "2",
            label: t("idwalSidebar.inspect"),
            items: [
                ...(inspections.flags.showInspectionsMyReports
                    ? [
                          {
                              id: "2-1",
                              label: t("appNavigation.myReports"),
                              className: isActive(
                                  inspections.links.myReportsLink
                              )
                                  ? "active"
                                  : "",
                              icon: (
                                  <SidebarIcon
                                      src={myReportsIcon}
                                      fill={
                                          isActive(
                                              inspections.links.myReportsLink
                                          )
                                              ? activeIconColor
                                              : nonActiveIconColor
                                      }
                                      style={idwalSidebarIconStyle}
                                  />
                              ),
                              command: () =>
                                  inspections.callbacks.clickInspectionsMyReports(
                                      inspections.links.myReportsLink
                                  )
                          }
                      ]
                    : []),
                ...(inspections.flags.showOrders
                    ? [
                          {
                              id: "2-1",
                              label: t("appNavigation.orders"),
                              className: isActive(
                                  inspections.links.inspectionOrdersLink
                              )
                                  ? "active"
                                  : "",
                              icon: (
                                  <SidebarIcon
                                      src={orders}
                                      fill={
                                          isActive(
                                              inspections.links
                                                  .inspectionOrdersLink
                                          )
                                              ? activeIconColor
                                              : nonActiveIconColor
                                      }
                                      style={idwalSidebarIconStyle}
                                  />
                              ),
                              command: () =>
                                  inspections.callbacks.clickInspectionsOrders(
                                      inspections.links.inspectionOrdersLink
                                  )
                          }
                      ]
                    : []),
                ...(inspections.flags.showInspectionsQuotes
                    ? [
                          {
                              id: "2-2",
                              label: t("appNavigation.quotes"),
                              className: isActive(
                                  inspections.links.inspectionQuotesLink
                              )
                                  ? "active"
                                  : "",
                              icon: (
                                  <SidebarIcon
                                      src={quotes}
                                      fill={
                                          isActive(
                                              inspections.links
                                                  .inspectionQuotesLink
                                          )
                                              ? activeIconColor
                                              : nonActiveIconColor
                                      }
                                      style={idwalSidebarIconStyle}
                                  />
                              ),
                              command: () =>
                                  inspections.callbacks.clickInspectionsQuotes(
                                      inspections.links.inspectionQuotesLink
                                  )
                          }
                      ]
                    : [])
            ]
        });
    }

    if (
        monitoring.flags.showMonitoringDefects ||
        monitoring.flags.showMonitoringGrades ||
        monitoring.flags.showMonitoringMyVessels ||
        monitoring.flags.showMonitoringSubGrades
    ) {
        idwalSidebarItems.items?.push({
            id: "3",
            label: t("idwalSidebar.monitor"),
            items: [
                ...(monitoring.flags.showMonitoringMyVessels &&
                additionalCapabilities["CAP_FLEETAPP_VIEW_FLEETS"] === true
                    ? [
                          {
                              id: "3-1",
                              label: t("appNavigation.myFleet"),
                              className: isActive(
                                  monitoring.links.myVesselsLink
                              )
                                  ? "active"
                                  : "",
                              icon: (
                                  <SidebarIcon
                                      src={myVesselsIcon}
                                      fill={
                                          isActive(
                                              monitoring.links.myVesselsLink
                                          )
                                              ? activeIconColor
                                              : nonActiveIconColor
                                      }
                                      style={idwalSidebarIconStyle}
                                  />
                              ),
                              command: () =>
                                  monitoring.callbacks.clickMonitoringMyVessels(
                                      monitoring.links.myVesselsLink
                                  )
                          }
                      ]
                    : []),
                ...(monitoring.flags.showMonitoringGrades &&
                additionalCapabilities["CAP_FLEETAPP_VIEW_GRADES"] === true
                    ? [
                          {
                              id: "3-2",
                              label: t("appNavigation.grade"),
                              className: isActive(monitoring.links.gradesLink)
                                  ? "active"
                                  : "",
                              icon: (
                                  <SidebarIcon
                                      src={gradeIcon}
                                      fill={
                                          isActive(monitoring.links.gradesLink)
                                              ? activeIconColor
                                              : nonActiveIconColor
                                      }
                                      style={idwalSidebarIconStyle}
                                  />
                              ),
                              command: () =>
                                  monitoring.callbacks.clickMonitoringGrades(
                                      monitoring.links.gradesLink
                                  )
                          }
                      ]
                    : []),
                ...(monitoring.flags.showMonitoringSubGrades &&
                additionalCapabilities["CAP_FLEETAPP_VIEW_SUBGRADES"] === true
                    ? [
                          {
                              id: "3-2",
                              label: t("appNavigation.subGrade"),
                              className: isActive(
                                  monitoring.links.subgradesLink
                              )
                                  ? "active"
                                  : "",
                              icon: (
                                  <SidebarIcon
                                      src={subgradeIcon}
                                      fill={
                                          isActive(
                                              monitoring.links.subgradesLink
                                          )
                                              ? activeIconColor
                                              : nonActiveIconColor
                                      }
                                      style={idwalSidebarIconStyle}
                                  />
                              ),
                              command: () =>
                                  monitoring.callbacks.clickMonitoringSubGrades(
                                      monitoring.links.subgradesLink
                                  )
                          }
                      ]
                    : []),
                ...(monitoring.flags.showMonitoringDefects &&
                additionalCapabilities["CAP_FLEETAPP_VIEW_DEFECTS"] === true
                    ? [
                          {
                              id: "3-2",
                              label: t("appNavigation.defects"),
                              icon: "pi pi-exclamation-triangle",
                              className: isActive(monitoring.links.defectsLink)
                                  ? "active"
                                  : "",
                              command: () =>
                                  monitoring.callbacks.clickMonitoringDefects(
                                      monitoring.links.defectsLink
                                  )
                          }
                      ]
                    : []),
                ...(additionalCapabilities[
                    "CAP_FLEETAPP_VIEW_RECTIFICATIONS"
                ] === true
                    ? [
                          {
                              id: "3-2",
                              label: t("appNavigation.rectifications"),
                              icon: "pi pi-exclamation-triangle",
                              className: isActive(
                                  monitoring.links.rectificationsLink
                              )
                                  ? "active"
                                  : "",
                              command: () =>
                                  monitoring.callbacks.clickMonitoringRetifications(
                                      monitoring.links.rectificationsLink
                                  )
                          }
                      ]
                    : [])
            ]
        });
    }

    return {
        appSidebarItems,
        idwalSidebarItems
    };
};
